/**
 * Resultado de una operación envuelta en try-catch.
 * - En caso de éxito: `[null, T]`
 * - En caso de error:  `[E, null]`
 */
export type TryCatchResult<T, E = Error> = [null, T] | [E, null];

// ─── Async ───────────────────────────────────────────────────────────────────

/**
 * Envuelve una `Promise` en un try-catch y devuelve una tupla `[error, data]`.
 *
 * @example
 * const [err, user] = await tryCatch(userService.findById(id));
 * if (err) throw new NotFoundException('Usuario no encontrado');
 * return user;
 */
export async function tryCatch<T, E = Error>(promise: Promise<T>): Promise<TryCatchResult<T, E>>;

/**
 * Envuelve una función **async** en un try-catch y devuelve una tupla `[error, data]`.
 *
 * @example
 * const [err, result] = await tryCatch(() => mailerService.send(dto));
 * if (err) logger.error('No se pudo enviar el correo', err);
 */
export async function tryCatch<T, E = Error>(fn: () => Promise<T>): Promise<TryCatchResult<T, E>>;

export async function tryCatch<T, E = Error>(
  input: Promise<T> | (() => Promise<T>)
): Promise<TryCatchResult<T, E>> {
  try {
    const data = await (typeof input === 'function' ? input() : input);
    return [null, data];
  } catch (error) {
    return [error as E, null];
  }
}

// ─── Sync ─────────────────────────────────────────────────────────────────────

/**
 * Envuelve una función **síncrona** en un try-catch y devuelve una tupla `[error, data]`.
 *
 * @example
 * const [err, parsed] = tryCatchSync(() => JSON.parse(rawJson));
 * if (err) return defaultValue;
 * return parsed;
 */
export function tryCatchSync<T, E = Error>(fn: () => T): TryCatchResult<T, E> {
  try {
    return [null, fn()];
  } catch (error) {
    return [error as E, null];
  }
}
